import { useMemo } from 'react';
import type {
    CaseDeclinedKpi,
    CaseKpis,
    CaseVersionKpi,
    ConversationKpis,
    TeamStatsBucket
} from '../../services/homeStats';
import { KpiCard } from '../billing/overview/OverviewCard';
import { ChartSkeleton } from '../shared/ChartSkeleton';
import { KPI_TREND_HEIGHT_PX, KpiTrendChart, TeamTrendChart, type KpiSeries } from './HomeCharts';
import { MutedNote } from './TeamWorkloadCard';
import {
    formatConversationFlow,
    formatCount,
    labelSeries,
    readTrackingNote
} from './teamStatsModel';

const STRIP_CLASSES = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4';
const NO_VALUE = 'n/a';

const LABELS = {
    submitted: 'Cases submitted',
    approved: 'Cases approved',
    declined: 'Cases declined',
    conversations: 'Conversations'
} as const;

const INFO = {
    submitted:
        'Cases by submission date in the selected range, test cases excluded. V1 and V2 split as on the Executive dashboard.',
    approved:
        'Cases by approval date in the selected range, test cases excluded. V1 and V2 split as on the Executive dashboard.',
    declined: 'Decline events in the selected range, counted per day as on the Clinical dashboard.',
    conversations:
        "Waiting for reply: practice conversations whose latest event is a practice message or 'mark as unread', not yet answered by an ODO person or marked as read. In: practice conversations with at least one practice message that day. Treated: conversations where an ODO person replied to a pending practice message that day. Median first reply: clock time from the practice's first unanswered message to the ODO reply (nights and weekends included). AI and system messages are ignored; test patients excluded."
} as const;

// Grafana's convention: submitted blue, approved green, declined red; V1 light, V2 dark.
const SUBMITTED_BARS = versionBars('#93c5fd', '#2563eb'); // blue-300, blue-600
const APPROVED_BARS = versionBars('#86efac', '#16a34a'); // green-300, green-600
const DECLINED_BARS: readonly KpiSeries[] = [
    { dataKey: 'count', name: 'Declined', color: '#ef4444' } // red-500
];

// Brand teal, as on the monitoring overview's open backlog.
const OPEN_TREND_COLOR = '#007E87';

function versionBars(v1Color: string, v2Color: string): readonly KpiSeries[] {
    return [
        { dataKey: 'v1', name: 'V1', color: v1Color },
        { dataKey: 'v2', name: 'V2', color: v2Color }
    ];
}

function VersionCount({ bar, value }: { bar: KpiSeries; value: number }) {
    return (
        <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: bar.color }} />
            {bar.name}
            <span className="font-semibold tabular-nums text-gray-600">{formatCount(value)}</span>
        </span>
    );
}

interface VersionTileProps {
    kind: 'submitted' | 'approved';
    kpi: CaseVersionKpi;
    bars: readonly KpiSeries[];
    granularity: TeamStatsBucket;
}

function VersionTile({ kind, kpi, bars, granularity }: VersionTileProps) {
    const points = useMemo(() => labelSeries(kpi.series, granularity), [kpi.series, granularity]);
    const [v1Bar, v2Bar] = bars;
    return (
        <KpiCard
            label={LABELS[kind]}
            info={INFO[kind]}
            value={formatCount(kpi.total)}
            foot={
                <span className="inline-flex items-center gap-1.5">
                    <VersionCount bar={v1Bar} value={kpi.v1} />·
                    <VersionCount bar={v2Bar} value={kpi.v2} />
                </span>
            }
        >
            <KpiTrendChart points={points} bars={bars} />
        </KpiCard>
    );
}

function DeclinedTile({
    kpi,
    granularity
}: {
    kpi: CaseDeclinedKpi;
    granularity: TeamStatsBucket;
}) {
    const points = useMemo(() => labelSeries(kpi.series, granularity), [kpi.series, granularity]);
    return (
        <KpiCard label={LABELS.declined} info={INFO.declined} value={formatCount(kpi.total)}>
            <KpiTrendChart points={points} bars={DECLINED_BARS} />
        </KpiCard>
    );
}

function ConversationsTile({
    kpi,
    granularity
}: {
    kpi: ConversationKpis;
    granularity: TeamStatsBucket;
}) {
    const points = useMemo(
        () => labelSeries(kpi.openSeries, granularity),
        [kpi.openSeries, granularity]
    );
    const flow = formatConversationFlow(kpi);
    const note = readTrackingNote(kpi.openReliableFrom, points);
    return (
        <KpiCard
            label={LABELS.conversations}
            info={INFO.conversations}
            value={
                <span>
                    {formatCount(kpi.openNow)}
                    <span className="text-sm font-medium text-gray-500"> waiting for reply</span>
                </span>
            }
            foot={
                // The caveat lives in the tooltip so it never adds a line to the strip.
                <span title={note ? `${flow}\n${note}` : flow}>
                    {note && <span className="text-amber-600">* </span>}
                    {flow}
                </span>
            }
        >
            <TeamTrendChart points={points} color={OPEN_TREND_COLOR} height={KPI_TREND_HEIGHT_PX} />
        </KpiCard>
    );
}

interface CaseKpiStripProps {
    kpis: CaseKpis;
    conversations: ConversationKpis;
    granularity: TeamStatsBucket;
}

export function CaseKpiStrip({ kpis, conversations, granularity }: CaseKpiStripProps) {
    return (
        <div className={STRIP_CLASSES}>
            <VersionTile
                kind="submitted"
                kpi={kpis.submitted}
                bars={SUBMITTED_BARS}
                granularity={granularity}
            />
            <VersionTile
                kind="approved"
                kpi={kpis.approved}
                bars={APPROVED_BARS}
                granularity={granularity}
            />
            {kpis.declined ? (
                <DeclinedTile kpi={kpis.declined} granularity={granularity} />
            ) : (
                <KpiCard label={LABELS.declined} info={INFO.declined} value={NO_VALUE}>
                    <MutedNote>Needs BigQuery analytics, not connected here.</MutedNote>
                </KpiCard>
            )}
            <ConversationsTile kpi={conversations} granularity={granularity} />
        </div>
    );
}

export function CaseKpiStripSkeleton() {
    return (
        <div className={STRIP_CLASSES}>
            {Object.values(LABELS).map((label) => (
                <KpiCard
                    key={label}
                    label={label}
                    value={
                        <span className="inline-block h-[26px] w-12 animate-pulse rounded bg-gray-100" />
                    }
                >
                    <div style={{ height: KPI_TREND_HEIGHT_PX }}>
                        <ChartSkeleton />
                    </div>
                </KpiCard>
            ))}
        </div>
    );
}
